import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import {
  IQuickReplyTemplate,
  QUICK_REPLY_TEMPLATE_BODY_MAX_LENGTH,
  QUICK_REPLY_TEMPLATE_NAME_MAX_LENGTH,
  QuickReplyTemplateData,
  QuickReplyTemplateErrorCode
} from 'shared/entities/quickReplyTemplate';
import {
  validateIsEmpty,
  validateTextFieldMaxLength
} from 'shared/entities/validator';
import { FieldModel } from 'shared/models/form';
import QuickReplyTemplatesStore from 'stores/quickReplyTemplatesStore/QuickReplyTemplatesStore';

type QuickReplyTemplateFormMode = 'create' | 'edit';

const validateTextFieldMaxLengthFactory =
  (maxLength: number) => (value: string) =>
    validateTextFieldMaxLength(value, maxLength);

/**
 * Локальный store формы create/edit шаблона (useLocal в модалке).
 * Loading create/edit — на QuickReplyTemplatesStore (creatingStage / editingStage).
 */
export default class QuickReplyTemplateFormStore {
  readonly name = new FieldModel<string>('', [
    validateIsEmpty,
    validateTextFieldMaxLengthFactory(QUICK_REPLY_TEMPLATE_NAME_MAX_LENGTH)
  ]);
  readonly body = new FieldModel<string>('', [
    validateIsEmpty,
    validateTextFieldMaxLengthFactory(QUICK_REPLY_TEMPLATE_BODY_MAX_LENGTH)
  ]);

  readonly mode: QuickReplyTemplateFormMode;

  private _notFoundOnSave = false;
  private templateId: string | null = null;

  constructor(
    private readonly quickReplyTemplatesStore: QuickReplyTemplatesStore,
    mode: QuickReplyTemplateFormMode
  ) {
    this.mode = mode;

    makeObservable<QuickReplyTemplateFormStore, '_notFoundOnSave'>(this, {
      _notFoundOnSave: observable,

      notFoundOnSave: computed,
      isError: computed,
      hasChanges: computed,
      isSaving: computed,
      canSave: computed,
      values: computed,

      reset: action.bound,
      updateEntity: action.bound,
      save: action.bound,
      changeNotFoundOnSave: action.bound
    });
  }

  get notFoundOnSave(): boolean {
    return this._notFoundOnSave;
  }

  get isError(): boolean {
    return this.name.isError || this.body.isError;
  }

  get hasChanges(): boolean {
    return this.name.touched || this.body.touched;
  }

  get isSaving(): boolean {
    return this.mode === 'create'
      ? this.quickReplyTemplatesStore.templates.creatingStage.isLoading
      : this.quickReplyTemplatesStore.editingStage.isLoading;
  }

  get canSave(): boolean {
    return this.hasChanges && !this.isSaving && !this.isError;
  }

  get values(): QuickReplyTemplateData {
    return {
      name: this.name.value,
      body: this.body.value
    };
  }

  validate(): boolean {
    this.name.validate();
    this.body.validate();

    return this.isError;
  }

  reset(prefill?: QuickReplyTemplateData): void {
    this.templateId = null;
    this._notFoundOnSave = false;

    this.setFields({
      name: prefill?.name ?? '',
      body: prefill?.body ?? ''
    });

    const hasPrefill = !!prefill?.name.trim() || !!prefill?.body.trim();

    if (hasPrefill) {
      this.name.setTouched();
      this.body.setTouched();
    }
  }

  updateEntity(template: IQuickReplyTemplate): void {
    this.templateId = template.id;
    this._notFoundOnSave = false;

    this.setFields({
      name: template.name,
      body: template.body
    });
  }

  changeNotFoundOnSave(value: boolean): void {
    this._notFoundOnSave = value;
  }

  async save(): Promise<
    BaseResponse<null, { errorCode: QuickReplyTemplateErrorCode | null }>
  > {
    this.validate();

    if (!this.canSave) {
      return { isError: true, data: { errorCode: null } };
    }

    this._notFoundOnSave = false;

    const payload: QuickReplyTemplateData = {
      name: this.name.value.trim(),
      body: this.body.value.trim()
    };

    const response =
      this.mode === 'create'
        ? await this.quickReplyTemplatesStore.create(payload)
        : await this.quickReplyTemplatesStore.update(this.templateId!, payload);

    return runInAction(() => {
      if (!response.isError) {
        return { isError: false, data: null };
      }

      const errorCode = response.data?.errorCode ?? null;

      if (errorCode === QuickReplyTemplateErrorCode.templateNotFound) {
        this._notFoundOnSave = true;
      }

      return { isError: true, data: { errorCode } };
    });
  }

  private setFields(values: QuickReplyTemplateData): void {
    this.name.changeValue(values.name);
    this.body.changeValue(values.body);
    this.name.resetError();
    this.body.resetError();
    this.name.resetTouched();
    this.body.resetTouched();
  }
}
